import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, PaginationQueryWithFields, Version, Versions } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { CostTypeCategory, CostTypeCategoryVersion, UpsertCostTypeCategory } from "./types";

export const useCostTypeCategoriesApi = () => {
  const api = useApi();

  const getAdminCostTypeCategory = useCallback(
    (costTypeCategoryId: string, signal: AbortSignal) => {
      const url = `/admin/cost_type_categories/${costTypeCategoryId}`;
      return useSWRMutation<CostTypeCategory | null, CustomError, string>(url, (url) =>
        api.get<CostTypeCategory>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminCostTypeCategories = useCallback(
    <K extends keyof CostTypeCategory>(
      query: PaginationQueryWithFields<Pick<CostTypeCategory, K>>,
      signal: AbortSignal,
    ) => {
      const url = buildUrl<Pick<CostTypeCategory, K>>("/admin/cost_type_categories", query);
      return useSWR<Paginated<Pick<CostTypeCategory, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Pick<CostTypeCategory, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminCostTypeCategory = useCallback(
    async (body: UpsertCostTypeCategory): Promise<{ id: string }> => {
      const url = `/admin/cost_type_categories`;
      const id = await api.post<UpsertCostTypeCategory, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminCostTypeCategory = useCallback(
    async (costTypeCategoryId: string, body: UpsertCostTypeCategory): Promise<void> => {
      const url = `/admin/cost_type_categories/${costTypeCategoryId}`;
      (await api.put<UpsertCostTypeCategory>(url, body),
        api.mutateCache(["/admin/cost_type_categories", `/admin/cost_type_categories/${costTypeCategoryId}/versions`]));
    },
    [api],
  );

  const deleteAdminCostTypeCategory = useCallback(
    async (costTypeCategoryId: string): Promise<void> => {
      const url = `/admin/cost_type_categories/${costTypeCategoryId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/cost_type_categories`]);
      });
    },
    [api],
  );

  const getAdminCostTypeCategoryHistory = useCallback(
    (query: PaginationQuery, costTypeCategoryID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/cost_type_categories/${costTypeCategoryID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminCostTypeCategoryVersion = useCallback(
    (costTypeCategoryId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/cost_type_categories/${costTypeCategoryId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<CostTypeCategoryVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<CostTypeCategoryVersion>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    getAdminCostTypeCategory,
    useAdminCostTypeCategories,
    insertAdminCostTypeCategory,
    updateAdminCostTypeCategory,
    deleteAdminCostTypeCategory,
    getAdminCostTypeCategoryHistory,
    getAdminCostTypeCategoryVersion,
  };
};
